import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listUsers, createUser, updateUser, deleteUser, UserInfo } from '../api/client';

export function UsersPage() {
  const { userId } = useAuth();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // create form
  const [showCreate, setShowCreate] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // edit state
  const [editId, setEditId] = useState<string | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setUsers(await listUsers());
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      setError('');
      await createUser(newUsername, newPassword);
      setNewUsername('');
      setNewPassword('');
      setShowCreate(false);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdate = async (id: string) => {
    try {
      setError('');
      const updates: { username?: string; password?: string } = {};
      if (editUsername) updates.username = editUsername;
      if (editPassword) updates.password = editPassword;
      if (!updates.username && !updates.password) return;
      await updateUser(id, updates);
      setEditId(null);
      setEditUsername('');
      setEditPassword('');
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      setError('');
      await deleteUser(id);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete user');
    }
  };

  const startEdit = (user: UserInfo) => {
    setEditId(user.id);
    setEditUsername(user.username);
    setEditPassword('');
  };

  return (
    <div style={s.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={s.heading}>Users</h2>
        <button style={s.btn} onClick={() => setShowCreate(!showCreate)}>+ New User</button>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {showCreate && (
        <div style={s.card}>
          <div style={s.formRow}>
            <input
              style={s.input}
              placeholder="Username (min 3 chars)"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
            />
            <input
              style={s.input}
              type="password"
              placeholder="Password (min 6 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button style={s.btn} onClick={handleCreate}>Create</button>
            <button style={s.cancelBtn} onClick={() => { setShowCreate(false); setNewUsername(''); setNewPassword(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={s.muted}>Loading...</div> : users.length === 0 ? <div style={s.muted}>No users</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {users.map((user) => (
            <div key={user.id} style={s.card}>
              {editId === user.id ? (
                <>
                  <div style={s.formRow}>
                    <input
                      style={s.input}
                      placeholder="Username"
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                    />
                    <input
                      style={s.input}
                      type="password"
                      placeholder="New password (leave empty to keep)"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                    />
                    <button style={s.btn} onClick={() => handleUpdate(user.id)}>Save</button>
                    <button style={s.cancelBtn} onClick={() => setEditId(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{user.username}</strong>
                    {user.id === userId && <span style={s.youBadge}>you</span>}
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      Created: {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={s.editBtn} onClick={() => startEdit(user)}>Edit</button>
                    {user.id !== userId && (
                      <button style={s.delBtn} onClick={() => handleDelete(user.id)}>Delete</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  cancelBtn: { padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  editBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: 12, cursor: 'pointer' },
  delBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer' },
  muted: { color: '#888', fontSize: 14 },
  card: { padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' },
  formRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const },
  input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, flex: 1, minWidth: 150 },
  error: { marginBottom: 12, padding: '10px 14px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13 },
  youBadge: { marginLeft: 8, padding: '2px 8px', borderRadius: 10, background: '#e0e7ff', color: '#4f46e5', fontSize: 11, fontWeight: 600 },
};
